// Ya Tú Sabes PR taxonomy — the single source of truth for browse categories,
// subcategories, and how a stored listing is resolved to a category.
// Admin dropdowns, the advanced search, the map and the rails all read from here.

import { BEACH_CATEGORY } from "@/data/beaches";

// The island's roadside, counter-service eating. It leads the dining rails, so
// the name lives here and is referenced by the taxonomy, the rails and the
// landing-page carousel alike.
export const CHINCHORROS_KIOSKS = "Chinchorros & Kiosks";

// The plant-based bucket, named here so the rail and the taxonomy agree on it.
export const VEGAN_SUB = "Vegan & Vegetarian";

// The services directory browses as one whole collection rather than a run of
// rails, so its name lives here for the view that renders it.
export const SERVICES_CATEGORY = "Services";

export const TAXONOMY = [
  {
    name: "Gastronomy",
    slug: "gastronomy",
    subcategories: [
      // Chinchorros and kiosks lead the dining rails.
      CHINCHORROS_KIOSKS,
      "Comedores & Home-Style Cooking",
      "Puerto Rican Criollo",
      "Breakfast & Brunch",
      "Cafés & Specialty Coffee",
      "Bakeries & Pastries",
      "Desserts & Ice Cream",
      "Juice Bars & Smoothies",
      "Sandwiches & Deli",
      "Pizza",
      "Tacos & Mexican",
      "Sushi & Japanese",
      "Caribbean & BBQ",
      "Seafood",
      "Steakhouse",
      "Spanish & Tapas",
      VEGAN_SUB,
      "Fine Dining",
      "Buffets & Family Style",
      "Rooftops & Views",
      "Bars & Lounges",
      "Cocktail Bars & Speakeasies",
      "Rooftop Bars",
      "Craft Breweries & Pubs",
      "Pubs & Sports Bars",
      "Breweries & Taprooms",
      "Wine Bars",
      "Wine & Mezcal Bars",
    ],
  },
  {
    name: "Culture",
    slug: "culture",
    subcategories: [
      "Museums",
      "Art Galleries",
      "Historic Forts & Citadels",
      "Historic Churches & Chapels",
      "Historic Sites & Monuments",
      "Plazas & Squares",
      "Parks & Gardens",
      "Botanical Gardens",
      "Public Art & Murals",
      "Architecture & Heritage",
      "Cultural Centers",
      "Theaters & Performance Venues",
      "Music Halls & Concert Venues",
      "Distilleries & Heritage Tours",
      "Libraries & Archives",
      "Science & Educational Centers",
      "Neighborhood Landmarks",
      "Cemeteries & Historic Grounds",
    ],
  },
  {
    name: "Experiences",
    slug: "experiences",
    subcategories: [
      "Party Bus & Karaoke Tours",
      "Bike & Scooter Rentals",
      "Boat Tours & Sailing",
      "Sunset & Catamaran Cruises",
      "Kayaking & Paddleboarding",
      "Snorkeling & Diving",
      "Surfing & Surf Lessons",
      "Beach Clubs & Day Passes",
      "Guided Walking Tours",
      "Food & Rum Tastings",
      "Hiking & Rainforest Tours",
      "Ziplining & Adventure Parks",
      "Horseback Riding",
      "Fishing Charters",
      "Photography Experiences",
      "Cultural Workshops & Classes",
      "Arcades & Retro Games",
      "Wellness & Retreats",
      "ATV & Off-Road Tours",
      "Horror & Haunted Experiences",
      "Day Trips & Excursions",
      "Party Bus & Karaoke Tours",
    ],
  },
  {
    name: "Nightlife",
    slug: "nightlife",
    subcategories: [
      // Nightlife is the entertainment side. Food and drink live under Gastronomy.
      "Live Music",
      "Salsa & Dance",
      "Nightclubs",
      "Karaoke & Piano Bars",
      "Billiards & Game Bars",
      "Casinos & Gaming",
      "LGBTQ+ Nightlife",
      "Late-Night Eats",
    ],
  },
  {
    name: "Shop",
    slug: "shop",
    subcategories: [
      "Botánicas & Spiritual Supplies",
      "Souvenirs & Gifts",
      "Local Apparel & Boutiques",
      "Jewelry & Accessories",
      "Artisan Crafts",
      "Independent Bookstores",
      "Music & Records",
      "Markets & Farmers Markets",
      "Grocery & Specialty Foods",
      "Coffee Roasters & Beans",
      "Rum & Spirits Shops",
      "Cigar Shops",
      "Surf & Outdoor Gear",
      "Beachwear & Swimwear",
      "Home Decor & Furniture",
      "Chocolate & Sweets",
      "Thrift & Vintage",
      "Pharmacy & Convenience",
      "Department Stores & Malls",
      "Pet Supplies",
    ],
  },
  {
    name: "LGBTQ+",
    slug: "lgbtq-plus",
    subcategories: [
      "LGBTQ+ Nightlife",
      "LGBTQ+ Dining",
      "LGBTQ+ Stays",
      "LGBTQ+ Community & Culture",
      "LGBTQ+ Services",
    ],
  },
  {
    name: "Services",
    slug: "services",
    subcategories: [
      "Party Buses & Group Transportation",
      "Barbershops",
      "Hair Salons & Stylists",
      "Nail Salons",
      "Spas & Massage",
      "Tattoo & Piercing Studios",
      "Fitness & Gyms",
      "Yoga & Pilates Studios",
      "Dance Studios",
      "Tailors & Alterations",
      "Repair & Handyman Services",
      "Photography & Videography",
      "Professional Services",
      "Legal & Notary Services",
      "Printing & Shipping",
      "Coworking Spaces",
      "Car Rentals & Transportation",
      "Car Wash & Detailing",
      "Tour Operators",
      "Laundry & Dry Cleaning",
      "Cleaning Services",
      "Event & Party Services",
      "Pet Services",
      "Wellness & Alternative Therapy",
      "Health & Medical Clinics",
      "Moving & Storage",
      "Luggage Storage",
    ],
  },
];

export const CATEGORIES = TAXONOMY.map((c) => c.name);

// The browse chips shown in the category rows: the beaches guide browses
// alongside the directory categories, right after the third one.
export const BROWSE_CHIPS = [...CATEGORIES.slice(0, 3), BEACH_CATEGORY, ...CATEGORIES.slice(3)];

// A small visual handle per browse chip, so the pills read as icon + word. Drawn
// from the same emoji set the map pins use (see PlacePin's EMOJI_MAP), so a
// category reads identically whether it shows on a chip or on the map.
export const CATEGORY_EMOJI = {
  Gastronomy: "🍴",
  Culture: "🏛️",
  Experiences: "🧭",
  [BEACH_CATEGORY]: "🏖️",
  Nightlife: "🍸",
  Shop: "🛍️",
  Services: "🛠️",
  "LGBTQ+": "🏳️‍🌈",
};

export const subcategoriesFor = (category) =>
  (TAXONOMY.find((c) => c.name === category) || {}).subcategories || [];

export const ALL_SUBCATEGORIES = Array.from(
  new Set(TAXONOMY.flatMap((c) => c.subcategories))
).sort();

// Directory zones, with map fly-to coordinates. The order here is the running
// order everywhere neighborhoods are listed — Río Piedras leads the browse rail.
export const NEIGHBORHOODS = [
  { name: "Río Piedras", slug: "rio-piedras", latitude: 18.4023, longitude: -66.0616 },
  { name: "Hato Rey", slug: "hato-rey", latitude: 18.4273, longitude: -66.0726 },
  { name: "Cataño", slug: "catano", latitude: 18.4411, longitude: -66.118 },
  { name: "Guaynabo", slug: "guaynabo", latitude: 18.3614, longitude: -66.1107 },
  { name: "Bayamón", slug: "bayamon", latitude: 18.3987, longitude: -66.1557 },
  { name: "Miramar", slug: "miramar", latitude: 18.4547, longitude: -66.0923 },
  { name: "Santurce", slug: "santurce", latitude: 18.453, longitude: -66.0848 },
  { name: "Carolina", slug: "carolina", latitude: 18.3808, longitude: -65.9574 },
  { name: "Old San Juan", slug: "old-san-juan", latitude: 18.4655, longitude: -66.1057 },
  { name: "Condado", slug: "condado", latitude: 18.4537, longitude: -66.0577 },
  // Zones the guide carries that the running order above does not name.
  { name: "Ocean Park", slug: "ocean-park", latitude: 18.4553, longitude: -66.0523 },
  { name: "Caguas", slug: "caguas", latitude: 18.2341, longitude: -66.0485 },
];

/* ------------------------------------------------------------------ *
 * Resolving a stored listing to a category
 * ------------------------------------------------------------------ */

const norm = (v) =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** Sort key that follows the curated NEIGHBORHOODS order (unknown zones go last). */
export const neighborhoodRank = (nb) => {
  const keys = [norm(nb?.slug), norm(nb?.name)].filter(Boolean);
  const i = NEIGHBORHOODS.findIndex((n) => keys.includes(norm(n.slug)) || keys.includes(norm(n.name)));
  return i === -1 ? NEIGHBORHOODS.length : i;
};

const CATEGORY_ALIASES = {
  eat: "Gastronomy",
  drink: "Gastronomy",
  gastronomy: "Gastronomy",
  nightlife: "Nightlife",
  shop: "Shop",
  shops: "Shop",
  shopping: "Shop",
  retail: "Shop",
  store: "Shop",
  stores: "Shop",
  experience: "Experiences",
  experiences: "Experiences",
  tours: "Experiences",
  "eat & drink": "Gastronomy",
  "eat and drink": "Gastronomy",
  "food & drink": "Gastronomy",
  food: "Gastronomy",
  dining: "Gastronomy",
  restaurant: "Gastronomy",
  restaurants: "Gastronomy",
  culture: "Culture",
  cultural: "Culture",
  night: "Nightlife",
  service: "Services",
  services: "Services",
  wellness: "Services",
  lgbtq: "LGBTQ+",
  "lgbtq+": "LGBTQ+",
  queer: "LGBTQ+",
};

const TYPE_CATEGORY = {
  restaurant: "Gastronomy",
  culture: "Culture",
  experience: "Experiences",
  nightlife: "Nightlife",
  shop: "Shop",
  service: "Services",
};

// Ordered most specific first: Culture is checked before Shop so a place like
// "Art Museum & Botanical Garden" can never land in Shop on the word "botanical".
const CATEGORY_HINTS = [
  [
    "Culture",
    /\b(museum|museo|gallery|galeria|fort|fortress|castle|citadel|historic|history|heritage|church|cathedral|chapel|plaza|park|garden|mural|sculpture|monument|architecture|cultural|theater|theatre|concert|library|distillery|landmark|cemetery)\b/,
  ],
  [
    "Gastronomy",
    /\b(restaurant|restaurante|food|eat|drink|cafe|coffee|bakery|panaderia|dining|dinner|lunch|breakfast|brunch|kitchen|grill|pizza|taco|sushi|paella|tapas|deli|criollo|creole|bbq|barbecue|dessert|juice|comedor|eatery|chinelo)\b/,
  ],
  [
    "Experiences",
    /\b(tour|tours|experience|boat|kayak|sail|sailing|snorkel|dive|diving|surf|adventure|hike|hiking|zipline|excursion|charter|paddle|haunted|horror)\b/,
  ],
  [
    "Nightlife",
    /\b(nightlife|night club|nightclub|salsa|karaoke|billiard|casino|disco|club|live music)\b/,
  ],
  [
    "Shop",
    /\b(shop|store|boutique|market|mercado|artisan|artisanal|souvenir|bookstore|bookseller|botanica|jewel|jewelry|cigar|grocery|pharmacy|mall|apparel|craft|record)\b/,
  ],
  [
    "LGBTQ+",
    /\b(lgbtq|queer|gay|lesbian|transgender|trans|sapphic|drag|bear tavern)\b/,
  ],
  [
    "Services",
    /\b(service|salon|barber|spa|massage|gym|fitness|yoga|repair|tailor|alteration|cowork|studio|laundry|rental|wellness|tattoo|photograph)\b/,
  ],
];

/** Free-text values already in the database, mapped to the curated subcategory. */
const LEGACY_SUBS = new Map(
  Object.entries({
    "Cafeteria": "Comedores & Home-Style Cooking",
    "Traditional Cafeteria": "Comedores & Home-Style Cooking",
    "Casual Criollo Eatery": "Puerto Rican Criollo",
    "Casual Dining & Criollo": "Puerto Rican Criollo",
    "Casual Restaurant": "Puerto Rican Criollo",
    "Puerto Rican Home-Style Cooking": "Comedores & Home-Style Cooking",
    "Coffee Shop & Bistro": "Cafés & Specialty Coffee",
    "Bakery & Cafe": "Bakeries & Pastries",
    "Bakery & Deli": "Bakeries & Pastries",
    "Bakery & Pastry Shop": "Bakeries & Pastries",
    "Artisanal Bakery": "Bakeries & Pastries",
    "Pizzeria": "Pizza",
    "Mexican Restaurant": "Tacos & Mexican",
    "Japanese Restaurant": "Sushi & Japanese",
    "Spanish Restaurant": "Spanish & Tapas",
    "Gourmet Sandwich & Brunch": "Sandwiches & Deli",
    "BBQ Restaurant": "Caribbean & BBQ",
    "Vegetarian Restaurant": "Vegan & Vegetarian",
    "Bar & Eatery": "Bars & Lounges",
    "Bar & Tapas": "Bars & Lounges",
    "Independent Bookseller": "Independent Bookstores",
    "Local Apparel & Bar": "Local Apparel & Boutiques",
    "Religious & Herbal Supplies": "Botánicas & Spiritual Supplies",
    "Mural": "Public Art & Murals",
    "Sculpture": "Public Art & Murals",
    "Adventure & Day Trips": "Day Trips & Excursions",
    // Snack stands, kiosks and food trucks are the same counter-service world as
    // a chinchorro, so the island's own name covers them.
    "Chinelos & Snack Stands": CHINCHORROS_KIOSKS,
    "Food Trucks & Kiosks": CHINCHORROS_KIOSKS,
    // Nightlife subcategories that used to be one combined bucket.
    "Live Music Venues": "Live Music",
    "Live Salsa & Latin Music": "Salsa & Dance",
  }).map(([k, v]) => [norm(k), v])
);

/**
 * The browse category a listing belongs to.
 * Curated `category` values win, then legacy free-text values, then `type`.
 */
export function listingCategory(listing) {
  const own = norm(listing?.category);
  const curated = CATEGORY_ALIASES[own];
  if (curated) return curated;

  const hay = norm(`${listing?.category || ""} ${listing?.subcategory || ""} ${listing?.name || ""}`);
  const hit = CATEGORY_HINTS.find(([, re]) => re.test(hay));
  if (hit) return hit[0];

  return TYPE_CATEGORY[norm(listing?.type)] || "";
}

const DRINK_SUBS = [
  "Bars & Lounges",
  "Cocktail Bars & Speakeasies",
  "Rooftop Bars",
  "Craft Breweries & Pubs",
  "Pubs & Sports Bars",
  "Breweries & Taprooms",
  "Wine Bars",
  "Wine & Mezcal Bars",
];

const NIGHTLIFE_SUBS = [
  "Live Music",
  "Salsa & Dance",
  "Nightclubs",
  "Karaoke & Piano Bars",
  "Billiards & Game Bars",
  "Casinos & Gaming",
  "LGBTQ+ Nightlife",
];

const matchesAnySub = (listing, subs) => subs.some((sub) => subMatches(listing, sub));

// LGBTQ+ discovery is cross-category: a drag venue can also be Nightlife,
// and a queer-owned café can remain Gastronomy in the main directory.
// Only explicitly tagged / described places qualify; this doesn't guess
// an owner's identity or imply that every venue in a neighborhood is queer.
const QUEER_RE = /\blgbtq?\+?\b|\bqueer\b|\bdrag\b|\bpride\b|\bsapphic\b|\bgay\b|\blesbian\b|\btransgender\b/i;
const LGBTQ_TYPES = [
  "LGBTQ+ Nightlife", "LGBTQ+ Dining", "LGBTQ+ Stays",
  "LGBTQ+ Community & Culture", "LGBTQ+ Services"
];
const queerType = (listing) => {
  const sub = norm(listing?.subcategory);
  const explicit = LGBTQ_TYPES.find((type) => sub === norm(type));
  if (explicit) return explicit;
  const hay = norm([
    listing?.name, listing?.category, listing?.subcategory, listing?.type,
    listing?.editorial_description, listing?.description, listing?.short_description,
    ...(Array.isArray(listing?.tags) ? listing.tags : []),
  ].filter(Boolean).join(" "));
  if (!QUEER_RE.test(hay)) return "";
  if (/bar|night|club|drag|cabaret|dance|karaoke|cocktail|lounge|pub/.test(hay)) return "LGBTQ+ Nightlife";
  if (/restaurant|cafe|coffee|food|dining|eat|brunch|bakery/.test(hay)) return "LGBTQ+ Dining";
  if (/hotel|stay|hostel|lodging|guesthouse|resort/.test(hay)) return "LGBTQ+ Stays";
  if (/shop|salon|barber|tattoo|service|store|boutique/.test(hay)) return "LGBTQ+ Services";
  return "LGBTQ+ Community & Culture";
};

export const catMatches = (listing, category) => {
  if (category === "Stays") {
    const hay = norm(`${listing?.type || ""} ${listing?.category || ""} ${listing?.subcategory || ""} ${listing?.name || ""}`);
    return /\b(stay|stays|lodging|hotel|hotels|hostel|hostels|inn|guesthouse|guest house|accommodation|accommodations|vacation rental|short term rental|resort|bed and breakfast|b\s*&\s*b|hospedaje|hospedajes|alojamiento|alojamientos|posada|posadas|airbnb)\b/.test(hay);
  }
  if (category === "LGBTQ+") return Boolean(queerType(listing));
  if (category === "Gastronomy") {
    const own = listingCategory(listing);
    return own === "Gastronomy" || ((own === "Nightlife" || own === "LGBTQ+") && matchesAnySub(listing, DRINK_SUBS));
  }
  if (category === "Nightlife") {
    return matchesAnySub(listing, NIGHTLIFE_SUBS) && !matchesAnySub(listing, DRINK_SUBS);
  }

  const own = listingCategory(listing);
  if (own) return own === category;
  // Nothing recognised — fall back to a loose text match so nothing disappears.
  const c = norm(category);
  return norm(listing?.category).includes(c) || norm(listing?.subcategory).includes(c);
};

// A chinchorro is recognised by what the place is: the stored values in the
// directory rarely use the word itself.
const CHINCHORRO_RE = /chinchorro|kiosko|\bkiosk|food truck|street food|snack stand|chinelo|fritura|roadside|open-air/;

export const subMatches = (listing, subcategory) => {
  const target = norm(subcategory);
  if (LGBTQ_TYPES.some((type) => norm(type) === target)) {
    return norm(queerType(listing)) === target;
  }

  // Bookable partner trips are never a local chinchorro, whatever their copy says.
  if (!listing?.source && target === norm(CHINCHORROS_KIOSKS)) {
    const hay = norm(
      `${listing?.name || ""} ${listing?.subcategory || ""} ${listing?.category || ""} ` +
        `${listing?.editorial_description || ""} ${listing?.description || ""}`
    );
    if (CHINCHORRO_RE.test(hay)) return true;
  }

  const sub = norm(listing?.subcategory);
  if (!sub) return false;
  if (sub === target) return true;

  const legacy = LEGACY_SUBS.get(sub);
  if (legacy && norm(legacy) === target) return true;

  // Legacy drift: compare on the head word ("Museums" -> "museum").
  const head = target.replace(/\s*&\s*.*$/, "").replace(/s$/, "");
  return head.length > 4 && sub.includes(head);
};

/* ------------------------------------------------------------------ *
 * Resolving a stored listing to a neighborhood
 * ------------------------------------------------------------------ */

// Neighborhoods are stored loosely: a record id on a few listings, and the
// area's own words on most ("Río Piedras", "El Cerezal / Río Piedras"). These
// are the ones that never name their curated zone.
const ZONE_ALIASES = new Map([
  ["university avenue", "Río Piedras"],
  ["paseo de diego", "Río Piedras"],
  ["plaza del mercado", "Río Piedras"],
  ["puerta de tierra", "Old San Juan"],
]);

// Hyphens and underscores read as spaces, so a stored slug compares like a name.
const loose = (v) => norm(String(v ?? "").replace(/[-_]+/g, " "));

/** The curated zone a stored neighborhood value belongs to, by name. */
export function resolveZone(value) {
  const v = loose(value);
  if (!v) return "";
  const curated = NEIGHBORHOODS.find((n) => v.includes(loose(n.name)));
  return curated ? curated.name : ZONE_ALIASES.get(v) || "";
}

/**
 * Does a listing sit in the selected zone? The stored value can be the
 * neighborhood's own record id, its slug, or the words of a sub-area, so all
 * three are read before the zone is considered a miss.
 */
export const zoneMatches = (listing, zone) => {
  const value = listing?.neighborhood_id;
  if (!value || !zone) return false;
  if (value === zone.id || value === zone.slug) return true;
  const resolved = resolveZone(value);
  // Compared loosely: the stored zone records spell their names without the
  // accents the curated list uses ("Rio Piedras" / "Río Piedras").
  return !!resolved && loose(resolved) === loose(zone.name);
};